import { BaseModule } from './baseModule';
import type { AxiosInstance } from 'axios';
import type { ErrorHandler } from './errorHandler';
import type {
  JobTemplate,
  CreateJobTemplateRequest,
  UpdateJobTemplateRequest,
  JobTemplateListResponse,
  JobTemplateGroupedResponse,
  MessageResponse,
  RequestConfig,
  AITemplateContext,
} from './types';
import { isAITemplate } from './types';

/**
 * Job Templates Module - Handles all job template-related API operations
 * 
 * This module provides job template management operations including CRUD operations
 * and filtering between system and custom templates.
 */
export class JobTemplatesModule extends BaseModule {
  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    super(httpClient, errorHandler);
  }

  // ============================================================================
  // Job Template CRUD Operations
  // ============================================================================

  /**
   * Get all templates (system + custom)
   * GET /api/admin/job-templates
   * Backend returns grouped response, we flatten it to an array
   */
  async getTemplates(config?: RequestConfig): Promise<JobTemplateListResponse> {
    const response = await this.get<JobTemplateGroupedResponse>('/api/admin/job-templates', config);
    return [
      ...(response.systemTemplates || []),
      ...(response.companyTemplates || [])
    ];
  }

  /**
   * Get template by ID
   * GET /api/admin/job-templates/:id
   */
  async getTemplateById(id: string, config?: RequestConfig): Promise<JobTemplate> {
    return this.get<JobTemplate>(`/api/admin/job-templates/${id}`, config);
  }

  /**
   * Create a custom template
   * POST /api/admin/job-templates
   */
  async createTemplate(data: CreateJobTemplateRequest, config?: RequestConfig): Promise<JobTemplate> {
    const transformedData = this.transformRequest(data);
    return this.post<JobTemplate>('/api/admin/job-templates', transformedData, config);
  }

  /**
   * Update a custom template
   * PUT /api/admin/job-templates/:id
   */
  async updateTemplate(id: string, data: UpdateJobTemplateRequest, config?: RequestConfig): Promise<JobTemplate> {
    const transformedData = this.transformRequest(data);
    return this.put<JobTemplate>(`/api/admin/job-templates/${id}`, transformedData, config);
  }

  /**
   * Delete a custom template
   * DELETE /api/admin/job-templates/:id
   */
  async deleteTemplate(id: string, config?: RequestConfig): Promise<MessageResponse> {
    return this.delete<MessageResponse>(`/api/admin/job-templates/${id}`, config);
  }

  // ============================================================================
  // Template Filtering Methods
  // ============================================================================

  /**
   * Get system templates only
   * GET /api/admin/job-templates?type=system
   * Backend returns grouped response, we extract systemTemplates
   */
  async getSystemTemplates(config?: RequestConfig): Promise<JobTemplateListResponse> {
    const response = await this.get<JobTemplateGroupedResponse>('/api/admin/job-templates?type=system', config);
    return response.systemTemplates || [];
  }

  /**
   * Get custom templates only
   * GET /api/admin/job-templates?type=custom
   * Backend returns grouped response, we extract companyTemplates
   */
  async getCustomTemplates(config?: RequestConfig): Promise<JobTemplateListResponse> {
    const response = await this.get<JobTemplateGroupedResponse>('/api/admin/job-templates?type=custom', config);
    return response.companyTemplates || [];
  }

  /**
   * Get templates by type (system or custom)
   */
  async getTemplatesByType(type: 'system' | 'custom', config?: RequestConfig): Promise<JobTemplateListResponse> {
    return this.get<JobTemplateListResponse>(`/api/admin/job-templates?type=${type}`, config);
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Get all templates grouped by type
   */
  async getTemplatesGrouped(config?: RequestConfig): Promise<{
    system: JobTemplate[];
    custom: JobTemplate[];
  }> {
    const allTemplates = await this.getTemplates(config);
    
    return {
      system: allTemplates.filter(t => t.template_type === 'system'),
      custom: allTemplates.filter(t => t.template_type === 'custom'),
    };
  }

  /**
   * Check if a template is a system template
   */
  isSystemTemplate(template: JobTemplate): boolean {
    return template.template_type === 'system';
  }

  /**
   * Check if a template is a custom template
   */
  isCustomTemplate(template: JobTemplate): boolean {
    return template.template_type === 'custom';
  }

  /**
   * Get template names for dropdown selection
   */
  async getTemplateOptions(config?: RequestConfig): Promise<Array<{ id: string; name: string; type: string }>> {
    const templates = await this.getTemplates(config);
    return templates.map(t => ({
      id: t.id,
      name: t.name,
      type: t.template_type,
    }));
  }

  /**
   * Get system template options only
   */
  async getSystemTemplateOptions(config?: RequestConfig): Promise<Array<{ id: string; name: string }>> {
    const templates = await this.getSystemTemplates(config);
    return templates.map(t => ({
      id: t.id,
      name: t.name,
    }));
  }

  /**
   * Get custom template options only
   */
  async getCustomTemplateOptions(config?: RequestConfig): Promise<Array<{ id: string; name: string }>> {
    const templates = await this.getCustomTemplates(config);
    return templates.map(t => ({
      id: t.id,
      name: t.name,
    }));
  }

  /**
   * Duplicate a template (create a new custom template from an existing one)
   */
  async duplicateTemplate(
    templateId: string,
    newName: string,
    config?: RequestConfig
  ): Promise<JobTemplate> {
    // Get the original template
    const originalTemplate = await this.getTemplateById(templateId, config);
    
    // Create a new custom template with the same job data
    const newTemplateData: CreateJobTemplateRequest = {
      name: newName,
      job_data: originalTemplate.job_data,
    };
    
    return this.createTemplate(newTemplateData, config);
  }

  /**
   * Search templates by name
   */
  async searchTemplates(query: string, config?: RequestConfig): Promise<JobTemplateListResponse> {
    const allTemplates = await this.getTemplates(config);
    
    const lowerQuery = query.toLowerCase();
    return allTemplates.filter(t => 
      t.name.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Get templates sorted by name
   */
  async getTemplatesSorted(ascending: boolean = true, config?: RequestConfig): Promise<JobTemplateListResponse> {
    const templates = await this.getTemplates(config);
    
    return templates.sort((a, b) => {
      const comparison = a.name.localeCompare(b.name);
      return ascending ? comparison : -comparison;
    });
  }

  /**
   * Get recently created custom templates
   */
  async getRecentCustomTemplates(limit: number = 5, config?: RequestConfig): Promise<JobTemplateListResponse> {
    const customTemplates = await this.getCustomTemplates(config);
    
    // Sort by created_at descending
    const sorted = customTemplates.sort((a, b) => {
      if (!a.created_at || !b.created_at) return 0;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    
    return sorted.slice(0, limit);
  }

  /**
   * Validate template data before creation/update
   */
  validateTemplateData(data: CreateJobTemplateRequest | UpdateJobTemplateRequest): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    
    if ('name' in data && data.name) {
      if (data.name.trim().length === 0) {
        errors.push('Template name cannot be empty');
      }
      if (data.name.length > 100) {
        errors.push('Template name cannot exceed 100 characters');
      }
    }
    
    if ('job_data' in data && data.job_data) {
      if (!data.job_data.title || data.job_data.title.trim().length === 0) {
        errors.push('Job title is required in template data');
      }
    }
    
    return {
      valid: errors.length === 0,
      errors,
    };
  }

  // ============================================================================
  // Company-Specific Template Operations
  // ============================================================================

  /**
   * Get templates for a specific company
   * GET /api/admin/job-templates?company_id=xxx
   * Returns grouped response with systemTemplates and companyTemplates filtered by company
   */
  async getCompanyTemplates(companyId: string, config?: RequestConfig): Promise<JobTemplateGroupedResponse> {
    // Backend returns { systemTemplates: [], companyTemplates: [] }
    // companyTemplates will be filtered to only show templates for this company
    return this.get<JobTemplateGroupedResponse>(`/api/admin/job-templates`, {
      ...config,
      params: { company_id: companyId },
    });
  }

  /**
   * Create a template for a specific company
   * POST /api/admin/job-templates (regular) or POST /api/admin/job-templates/ai (AI templates)
   */
  async createCompanyTemplate(
    companyId: string,
    data: CreateJobTemplateRequest,
    config?: RequestConfig
  ): Promise<JobTemplate> {
    // Check if this is an AI template
    if (data.template_type === 'ai' && data.ai_context) {
      // Use the AI template endpoint
      const aiData = {
        name: data.name,
        company_id: companyId,
        ai_context: data.ai_context,
      };
      const transformedData = this.transformRequest(aiData);
      return this.post<JobTemplate>('/api/admin/job-templates/ai', transformedData, config);
    }
    
    // Regular template - use the standard endpoint
    const dataWithCompany = {
      ...data,
      company_id: companyId,
    };
    const transformedData = this.transformRequest(dataWithCompany);
    return this.post<JobTemplate>(`/api/admin/job-templates`, transformedData, config);
  }

  /**
   * Get a specific company template
   * GET /api/admin/job-templates/:templateId
   */
  async getCompanyTemplate(
    _companyId: string,
    templateId: string,
    config?: RequestConfig
  ): Promise<JobTemplate> {
    return this.get<JobTemplate>(`/api/admin/job-templates/${templateId}`, config);
  }

  /**
   * Update a company template
   * PUT /api/admin/job-templates/:templateId
   */
  async updateCompanyTemplate(
    _companyId: string,
    templateId: string,
    data: UpdateJobTemplateRequest,
    config?: RequestConfig
  ): Promise<JobTemplate> {
    const transformedData = this.transformRequest(data);
    return this.put<JobTemplate>(
      `/api/admin/job-templates/${templateId}`,
      transformedData,
      config
    );
  }

  /**
   * Delete a company template
   * DELETE /api/admin/job-templates/:templateId
   */
  async deleteCompanyTemplate(
    _companyId: string,
    templateId: string,
    config?: RequestConfig
  ): Promise<MessageResponse> {
    return this.delete<MessageResponse>(
      `/api/admin/job-templates/${templateId}`,
      config
    );
  }

  /**
   * Get available templates for a company (system + company-specific)
   * GET /api/admin/job-templates/available?company_id=xxx
   * Backend returns grouped response, we flatten it to an array
   */
  async getAvailableTemplates(companyId?: string, config?: RequestConfig): Promise<JobTemplateListResponse> {
    const params = companyId ? { company_id: companyId } : {};
    const response = await this.get<JobTemplateGroupedResponse>('/api/admin/job-templates/available', {
      ...config,
      params,
    });
    return [
      ...(response.systemTemplates || []),
      ...(response.companyTemplates || [])
    ];
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Get templates filtered by company (for dropdown/selection)
   * Returns system templates + company-specific templates as a flat array
   */
  async getTemplatesForJobCreation(companyId?: string, config?: RequestConfig): Promise<JobTemplate[]> {
    // Get all templates - backend returns { systemTemplates: [], companyTemplates: [] }
    const response = await this.get<JobTemplateGroupedResponse>('/api/admin/job-templates', config);
    
    // Flatten the grouped response into a single array
    const allTemplates = [
      ...(response.systemTemplates || []),
      ...(response.companyTemplates || [])
    ];
    
    // If companyId is provided, filter to show only system templates + that company's templates
    if (companyId) {
      return allTemplates.filter(t => 
        t.template_type === 'system' || t.company_id === companyId
      );
    }
    
    return allTemplates;
  }

  // ============================================================================
  // AI Template Operations (Requirements 3.1, 5.1)
  // ============================================================================

  /**
   * Get templates for Job Composer (company-only, excludes system templates)
   * GET /api/admin/job-templates/composer?company_id={id}
   * 
   * This method returns only company-specific templates (both regular and AI),
   * excluding system templates from the dropdown as per Requirements 3.1, 6.1
   * 
   * @param companyId - The company ID to filter templates by
   * @param config - Optional request configuration
   * @returns Templates grouped by type (regular and AI)
   */
  async getJobComposerTemplates(
    companyId: string,
    config?: RequestConfig
  ): Promise<{
    regularTemplates: JobTemplate[];
    aiTemplates: JobTemplate[];
  }> {
    const response = await this.get<any>(
      '/api/admin/job-templates/composer',
      {
        ...config,
        params: { company_id: companyId },
      }
    );

    // Backend returns { customTemplates: [], aiTemplates: [], total: number }
    return {
      regularTemplates: response.customTemplates || [],
      aiTemplates: response.aiTemplates || [],
    };
  }

  /**
   * Create an AI template
   * POST /api/admin/job-templates
   * 
   * AI templates require a company_id and ai_context.
   * They contain context prompts for AI generation rather than static content.
   * 
   * @param data - AI template creation data including context prompts
   * @param config - Optional request configuration
   * @returns The created AI template
   */
  async createAITemplate(
    data: {
      name: string;
      company_id: string;
      ai_context: AITemplateContext;
    },
    config?: RequestConfig
  ): Promise<JobTemplate> {
    // AI templates use a separate endpoint that expects ai_context instead of job_data
    const requestData = {
      name: data.name,
      company_id: data.company_id,
      ai_context: data.ai_context,
    };
    
    const transformedData = this.transformRequest(requestData);
    return this.post<JobTemplate>('/api/admin/job-templates/ai', transformedData, config);
  }

  /**
   * Get AI template context for generation
   * GET /api/admin/job-templates/:id/context
   * 
   * Retrieves the AI context prompts from a template for use in generation.
   * 
   * @param templateId - The template ID to get context from
   * @param config - Optional request configuration
   * @returns The AI template context
   */
  async getAITemplateContext(
    templateId: string,
    config?: RequestConfig
  ): Promise<AITemplateContext> {
    const template = await this.getTemplateById(templateId, config);
    
    if (!isAITemplate(template)) {
      throw new Error('Template is not an AI template');
    }
    
    return template.ai_context || {};
  }

  /**
   * Update an AI template's context
   * PUT /api/admin/job-templates/:id
   * 
   * @param templateId - The template ID to update
   * @param context - The new AI context
   * @param config - Optional request configuration
   * @returns The updated template
   */
  async updateAITemplateContext(
    templateId: string,
    context: AITemplateContext,
    config?: RequestConfig
  ): Promise<JobTemplate> {
    const updateData: UpdateJobTemplateRequest = {
      ai_context: context,
    };
    
    return this.updateTemplate(templateId, updateData, config);
  }

  /**
   * Duplicate an AI template (copies all context prompts)
   * Requirements 5.4
   * 
   * @param templateId - The source template ID
   * @param newName - Name for the duplicated template
   * @param targetCompanyId - Optional company ID for the new template
   * @param config - Optional request configuration
   * @returns The duplicated template
   */
  async duplicateAITemplate(
    templateId: string,
    newName: string,
    targetCompanyId?: string,
    config?: RequestConfig
  ): Promise<JobTemplate> {
    const originalTemplate = await this.getTemplateById(templateId, config);
    
    if (!isAITemplate(originalTemplate)) {
      throw new Error('Template is not an AI template');
    }
    
    const newTemplateData: CreateJobTemplateRequest = {
      name: newName,
      company_id: targetCompanyId || originalTemplate.company_id,
      template_type: 'ai',
      ai_context: originalTemplate.ai_context,
    };
    
    const transformedData = this.transformRequest(newTemplateData);
    return this.post<JobTemplate>('/api/admin/job-templates', transformedData, config);
  }

  /**
   * Check if a template is an AI template
   */
  isAITemplate(template: JobTemplate): boolean {
    return isAITemplate(template);
  }

  /**
   * Validate AI template data before creation
   */
  validateAITemplateData(data: {
    name: string;
    company_id?: string;
    ai_context?: AITemplateContext;
  }): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    
    if (!data.name || data.name.trim().length === 0) {
      errors.push('Template name is required');
    }
    
    if (data.name && data.name.length > 100) {
      errors.push('Template name cannot exceed 100 characters');
    }
    
    if (!data.company_id) {
      errors.push('Company ID is required for AI templates');
    }
    
    if (!data.ai_context) {
      errors.push('AI context is required for AI templates');
    } else {
      // Check that at least one context field is provided
      const hasContext = 
        data.ai_context.title_context ||
        data.ai_context.description_context ||
        data.ai_context.requirements_context ||
        data.ai_context.benefits_context ||
        data.ai_context.educational_qualifications_context;
      
      if (!hasContext) {
        errors.push('At least one context field must be provided');
      }
    }
    
    return {
      valid: errors.length === 0,
      errors,
    };
  }
}
